import { ActionReducerMapBuilder, isAnyOf } from '@reduxjs/toolkit';
import {
  ScorecardStores,
  ScorecardFilters,
  Scorecard,
  ScorecardGoal,
  ScorecardSubGoal,
} from 'store/interfaces';
import { DepartmentState } from './department';
import { SubordinateState } from './subordinates';
import { DirectReportState } from './direct';
import { OwnScorecardsState } from './own';
import { SharedState } from './shared';
import { ProfileState } from './profiles';
import { UserScorecardsState } from './user';
import {
  changeFilter,
  changePage,
  changeStatus,
  setFullData,
  updateFullData,
  clearFilters,
  updateList,
  moderateFeedback,
  setFeedbackVisibility,
  patchScorecard,
  updateScorecardCommentsThread,
  fetchScorecardsByCycle,
} from './actions';

import { storeName as sharedStoreName } from './shared/config';
import mergeScorecardListItemIntoFullData from './mergeScorecardListItemIntoFullData';

type ScorecardStates =
  | DepartmentState
  | SubordinateState
  | DirectReportState
  | OwnScorecardsState
  | SharedState
  | ProfileState
  | UserScorecardsState;

export default function addScorecardsCases<T extends ScorecardStates>(
  builder: ActionReducerMapBuilder<T>,
  storeName: ScorecardStores,
  initialFilters: ScorecardFilters,
) {
  return builder
    .addCase(
      updateList.pending,
      (
        state,
        {
          meta: {
            arg: { store },
          },
        },
      ) => {
        if (store.indexOf(storeName) > -1) {
          state.loading = true;
        }
      },
    )
    .addCase(
      updateList.fulfilled,
      (
        state,
        {
          meta: {
            arg: { store },
          },
          payload,
        },
      ) => {
        if (store.indexOf(storeName) > -1) {
          // Skip state update for export requests (when payload is void)
          if (!payload) {
            state.loading = false;
            return;
          }

          const { list, pagination } = payload;
          if (!state.init && list.length > 0 && storeName === sharedStoreName) {
            (state as SharedState).hasSharedScorecards = true;
          }

          Object.assign(state, {
            items: list,
            pagination,
            loading: false,
            init: true,
          });

          list.forEach((item) => {
            if (state.fullData[item.id]) {
              Object.assign(state.fullData[item.id], {
                ...state.fullData[item.id],
                data: mergeScorecardListItemIntoFullData(
                  state.fullData[item.id].data as Scorecard,
                  item,
                ),
              });
            }
          });
        }
      },
    )
    .addCase(
      updateList.rejected,
      (
        state,
        {
          meta: {
            arg: { store },
          },
        },
      ) => {
        if (store.indexOf(storeName) > -1) {
          state.loading = false;
        }
      },
    )
    .addCase(
      fetchScorecardsByCycle.fulfilled,
      (
        state,
        {
          meta: {
            arg: { store },
          },
          payload,
        },
      ) => {
        // Upsert items returned by per-cycle fetch into state.items so
        // selectors that read state.items (e.g. selectListData → version
        // for optimistic locking) keep working when the listing is driven
        // by ReviewCycleGroup local state.
        if (store !== storeName || !payload?.list) return;
        payload.list.forEach((item) => {
          const idx = state.items.findIndex(
            (existing) => existing.id === item.id,
          );
          if (idx >= 0) {
            state.items[idx] = { ...state.items[idx], ...item };
          } else {
            state.items.push(item as never);
          }
        });
      },
    )
    .addCase(setFullData, (state, { payload: { store, data } }) => {
      if (store.indexOf(storeName) > -1) {
        const listItem = state.items.find((item) => item.id === data.id);
        if (listItem) {
          const { goals, feedbacks, feedbackRequests, ...listData } = data;
          Object.assign(listItem, listData);
        }
        state.fullData[data.id].data = data;
      }
    })
    .addCase(
      updateScorecardCommentsThread,
      (
        state,
        { payload: { store, scorecardId, goalId, subgoalId, thread, change } },
      ) => {
        if (store.indexOf(storeName) > -1) {
          const scorecard = state.fullData[scorecardId]?.data;
          if (scorecard?.goals) {
            const goal = scorecard.goals.find(
              (g: ScorecardGoal) => g.id === goalId,
            );
            if (goal) {
              if (subgoalId) {
                const subgoal = goal.subgoals?.find(
                  (s: ScorecardSubGoal) => s.id === subgoalId,
                );
                if (subgoal) {
                  if (change !== undefined && subgoal.commentThread) {
                    subgoal.commentThread.totalMessages = Math.max(
                      0,
                      subgoal.commentThread.totalMessages + change,
                    );
                  } else {
                    subgoal.commentThread = thread;
                  }
                }
              } else if (change !== undefined && goal.commentThread) {
                goal.commentThread.totalMessages = Math.max(
                  0,
                  goal.commentThread.totalMessages + change,
                );
              } else {
                goal.commentThread = thread;
              }
            }
          }
        }
      },
    )
    .addCase(clearFilters, (state, { payload: { store, data } }) => {
      if (store.indexOf(storeName) > -1) {
        const newFilters = { ...initialFilters };
        if (data) {
          data.forEach((filterName) => {
            if (Object.hasOwn(newFilters, filterName)) {
              // @ts-ignore
              delete newFilters[filterName];
            }
          });
        }
        Object.assign(state.filters, newFilters);
      }
    })
    .addCase(changeFilter, (state, { payload: { store, data } }) => {
      if (store.indexOf(storeName) > -1) {
        if ('key' in data && 'value' in data) {
          Object.assign(state, {
            filters: { ...state.filters, [data.key]: data.value },
            init: true,
          });
        } else {
          Object.assign(state, {
            filters: { ...state.filters, ...data },
            init: true,
          });
        }
      }
    })
    .addCase(changePage, (state, { payload: { store, page } }) => {
      if (store.indexOf(storeName) > -1) {
        state.pagination.page = page;
      }
    })
    .addMatcher(
      isAnyOf(
        updateFullData.pending,
        changeStatus.pending,
        moderateFeedback.pending,
        setFeedbackVisibility.pending,
        patchScorecard.pending,
      ),
      (
        state,
        {
          meta: {
            arg: { id, store },
          },
        },
      ) => {
        if (store.indexOf(storeName) > -1) {
          if (!state.fullData[id]) {
            state.fullData[id] = { loading: true, data: null };
          } else {
            state.fullData[id].loading = true;
          }
        }
      },
    )
    .addMatcher(
      isAnyOf(
        updateFullData.fulfilled,
        changeStatus.fulfilled,
        moderateFeedback.fulfilled,
        setFeedbackVisibility.fulfilled,
        patchScorecard.fulfilled,
      ),
      (
        state,
        {
          meta: {
            arg: { id, store },
          },
          payload,
        },
      ) => {
        if (store.indexOf(storeName) > -1) {
          const listItem = state.items.find((item) => item.id === id);
          if (listItem) {
            const { goals, feedbacks, feedbackRequests, ...listData } = payload;
            Object.assign(listItem, { ...listData, locked: false });
          }
          state.fullData[id] = {
            loading: false,
            data: payload,
          };
        }
      },
    )
    .addMatcher(
      isAnyOf(
        updateFullData.rejected,
        changeStatus.rejected,
        moderateFeedback.rejected,
        setFeedbackVisibility.rejected,
        patchScorecard.rejected,
      ),
      (
        state,
        {
          meta: {
            arg: { id, store },
          },
        },
      ) => {
        if (store.indexOf(storeName) > -1) {
          state.fullData[id].loading = false;
        }
      },
    )
    .addMatcher(
      isAnyOf(patchScorecard.rejected),
      (
        state,
        {
          meta: {
            arg: { id, store },
          },
          payload,
        },
      ) => {
        if (store.indexOf(storeName) > -1) {
          // Only flag the record as locked on 412 (optimistic-locking conflict).
          // Other failures (validation, 5xx) leave the record untouched so the
          // user can retry without losing their unsaved edits.
          const status = (payload as { response?: { status?: number } })
            ?.response?.status;
          if (status !== 412) return;
          const listItem = state.items.find((item) => item.id === id);
          if (listItem) {
            Object.assign(listItem, { ...listItem, locked: true });
          }
        }
      },
    );
}
